import React, { useEffect, useRef, useState } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Button,
  Tag,
  Space,
  Typography,
  Spin,
  Alert,
  Rate,
  Modal,
  Descriptions,
  DatePicker,
  Dropdown,
  List,
  Input,
  Divider,
  Avatar,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import {
  FilterOutlined,
  StarOutlined,
  ReloadOutlined,
  MoreOutlined,
  UserOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface FeedbackComment {
  id: string;
  author_type: 'customer' | 'agent';
  author_name: string;
  comment: string;
  created_at: string;
}

interface FeedbackItem {
  id: string;
  ticket_id: string;
  rating: number;
  comment: string;
  is_submitted: boolean;
  submitted_at: string;
  created_at: string;
  ticket_number: string;
  ticket_subject: string;
  assignee_first_name: string;
  assignee_last_name: string;
  follow_up_assignee_id?: string | null;
  follow_up_assignee_first_name?: string;
  follow_up_assignee_last_name?: string;
  follow_up_assignee_role?: string;
  follow_up_assignee_agent_level?: number | null;
  follow_up_assignee_role_display_name?: string;
  business_id: string;
  business_name: string;
  contact_person: string;
  business_email: string;
  archived_at?: string;
  is_archived?: boolean;
}

interface AgentOption {
  id: string;
  first_name: string;
  last_name: string;
  role?: string;
  agent_level?: number | null;
  role_display_name?: string;
}

const formatRoleLabel = (
  role?: string,
  agentLevel?: number | null,
  roleDisplayName?: string
): string => {
  if (roleDisplayName?.trim()) {
    return roleDisplayName.trim();
  }
  if (!role) return '';
  const roleName = role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ');
  if (role === 'agent' && agentLevel != null) {
    return `${roleName} (Level ${agentLevel})`;
  }
  return roleName;
};

const formatUserWithRole = (
  firstName?: string,
  lastName?: string,
  role?: string,
  agentLevel?: number | null,
  roleDisplayName?: string
): string => {
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  const roleLabel = formatRoleLabel(role, agentLevel, roleDisplayName);
  return roleLabel ? `${name} (${roleLabel})` : name;
};

const FEEDBACK_DELETE_ALLOWED_EMAIL = 'sunny.patel@vitreousco.com';

const FeedbackDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const followUpSectionRef = useRef<HTMLDivElement>(null);
  const canDeleteFeedback =
    String(user?.email || '').trim().toLowerCase() === FEEDBACK_DELETE_ALLOWED_EMAIL;
  
  const [filters, setFilters] = useState({
    is_submitted: undefined as boolean | undefined,
    rating: undefined as number | undefined,
    assignee_id: undefined as string | undefined
  });
  const [ratingFilter, setRatingFilter] = useState<'positive' | 'negative' | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [focusFollowUp, setFocusFollowUp] = useState(false);
  const [newComment, setNewComment] = useState('');
  
  // Get business_id and business_name from URL params
  const businessIdFromUrl = searchParams.get('business_id');
  const businessNameFromUrl = searchParams.get('business_name');
  const [storeTicketsModalVisible, setStoreTicketsModalVisible] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(businessIdFromUrl);
  const [selectedBusinessName, setSelectedBusinessName] = useState<string>(businessNameFromUrl || '');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  
  // Auto-open modal if business_id is in URL (e.g., when returning from a ticket)
  useEffect(() => {
    if (businessIdFromUrl && businessNameFromUrl) {
      setSelectedBusinessId(businessIdFromUrl);
      setSelectedBusinessName(businessNameFromUrl);
      setStoreTicketsModalVisible(true);
    }
  }, [businessIdFromUrl, businessNameFromUrl]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [filters, dateRange, ratingFilter]);

  const getFeedbackErrorMessage = (err: unknown) => {
    const axiosErr = err as { response?: { status?: number }; code?: string };
    if (axiosErr?.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }
    if (axiosErr?.response?.status === 403) {
      return 'You do not have permission to view feedback.';
    }
    if (axiosErr?.response?.status) {
      return `Server error (${axiosErr.response.status}). Please try again.`;
    }
    return 'Failed to load feedback data. Please try again.';
  };

  // Fetch feedback data
  const {
    data: feedbackData,
    isLoading,
    isFetching,
    error,
    refetch,
    isError,
  } = useQuery(
    ['feedback', filters, dateRange, ratingFilter, pagination.current, pagination.pageSize],
    async () => {
      const params: Record<string, string | number | boolean> = {
        page: pagination.current,
        limit: pagination.pageSize,
      };
      if (filters.is_submitted !== undefined) {
        params.is_submitted = filters.is_submitted;
      }
      if (filters.rating !== undefined) {
        params.rating = filters.rating;
      }
      if (filters.assignee_id) {
        params.assignee_id = filters.assignee_id;
      }
      if (ratingFilter) {
        params.rating_sentiment = ratingFilter;
      }
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.get('/api/feedback', { params });
      return response.data;
    },
    {
      enabled: !!user,
      retry: 2,
      keepPreviousData: true,
      refetchInterval: 10000,
    }
  );

  // Fetch agents for assignee filter
  const { data: agentsData } = useQuery(
    ['agents'],
    async () => {
      const response = await api.get('/api/users/agents');
      return response.data;
    }
  );

  // Fetch feedback statistics
  const { data: statsData } = useQuery(
    ['feedback-stats'],
    async () => {
      const response = await api.get('/api/feedback/stats');
      return response.data;
    }
  );

  // Fetch monthly feedback statistics
  const { data: monthlyStatsData } = useQuery(
    ['feedback-monthly-stats'],
    async () => {
      const response = await api.get('/api/feedback/stats/monthly');
      return response.data;
    }
  );

  const feedback = feedbackData?.items || [];
  const feedbackTotal = feedbackData?.total ?? 0;
  
  const stats = statsData;
  const agents = agentsData?.users || [];

  const {
    data: feedbackDetailData,
    isLoading: isLoadingDetail,
    refetch: refetchDetail,
  } = useQuery(
    ['feedback-detail', selectedFeedback?.id],
    async () => {
      const response = await api.get(`/api/feedback/records/${selectedFeedback!.id}`);
      return response.data;
    },
    {
      enabled: !!selectedFeedback?.id && modalVisible,
    }
  );

  const detailFeedback: FeedbackItem | null = feedbackDetailData?.feedback || selectedFeedback;
  const detailComments: FeedbackComment[] = feedbackDetailData?.comments || [];

  const followUpMutation = useMutation(
    async (assigneeId: string | null) => {
      const response = await api.patch(`/api/feedback/records/${selectedFeedback!.id}/follow-up`, {
        assignee_id: assigneeId,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Follow-up assignee updated');
        queryClient.invalidateQueries(['feedback']);
        refetchDetail();
      },
      onError: () => {
        message.error('Failed to update follow-up assignee');
      },
    }
  );

  const addCommentMutation = useMutation(
    async (comment: string) => {
      const response = await api.post(`/api/feedback/records/${selectedFeedback!.id}/comments`, {
        comment,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Comment added');
        setNewComment('');
        queryClient.invalidateQueries(['feedback']);
        refetchDetail();
      },
      onError: () => {
        message.error('Failed to add comment');
      },
    }
  );

  const deleteFeedbackMutation = useMutation(
    async (feedbackId: string) => {
      const response = await api.delete(`/api/feedback/records/${feedbackId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Feedback deleted');
        setModalVisible(false);
        setSelectedFeedback(null);
        setNewComment('');
        setFocusFollowUp(false);
        queryClient.invalidateQueries(['feedback']);
        queryClient.invalidateQueries(['feedback-stats']);
        queryClient.invalidateQueries(['feedback-monthly-stats']);
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.error || 'Failed to delete feedback');
      },
    }
  );

  const confirmDeleteFeedback = (record: FeedbackItem) => {
    Modal.confirm({
      title: 'Delete this feedback?',
      content: `Permanently delete feedback for ticket #${record.ticket_number} (${record.business_name})? This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => deleteFeedbackMutation.mutateAsync(record.id),
    });
  };

  useEffect(() => {
    if (modalVisible && focusFollowUp && followUpSectionRef.current) {
      followUpSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setFocusFollowUp(false);
    }
  }, [modalVisible, focusFollowUp, feedbackDetailData]);

  const openFeedbackModal = (record: FeedbackItem, focusAssignee = false) => {
    setSelectedFeedback(record);
    setModalVisible(true);
    setNewComment('');
    if (focusAssignee) {
      setFocusFollowUp(true);
    }
  };

  const closeFeedbackModal = () => {
    setModalVisible(false);
    setSelectedFeedback(null);
    setNewComment('');
    setFocusFollowUp(false);
  };

  const getFeedbackActionMenu = (record: FeedbackItem) => ({
    items: [
      {
        key: 'details',
        label: 'View Details',
        onClick: () => openFeedbackModal(record),
      },
      {
        key: 'ticket',
        label: 'View Ticket',
        onClick: () => navigate(`/workspace/number/${record.ticket_number}`),
      },
      {
        key: 'assign',
        label: 'Assign for Follow-up',
        disabled: !record.is_submitted,
        onClick: () => openFeedbackModal(record, true),
      },
      ...(canDeleteFeedback
        ? [
            {
              key: 'delete',
              label: 'Delete Feedback',
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => confirmDeleteFeedback(record),
            },
          ]
        : []),
    ],
  });

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'warning';
    return 'error';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTicketClick = (ticketNumber: string) => {
    navigate(`/workspace/number/${ticketNumber}`);
  };

  const handleStoreClick = (businessId: string, businessName: string) => {
    setSelectedBusinessId(businessId);
    setSelectedBusinessName(businessName);
    setStoreTicketsModalVisible(true);
    // Update URL params to persist state
    const params = new URLSearchParams(searchParams);
    params.set('business_id', businessId);
    params.set('business_name', businessName);
    setSearchParams(params, { replace: true });
  };

  // Fetch tickets for selected business
  const { data: storeTicketsData, isLoading: isLoadingStoreTickets } = useQuery(
    ['store-tickets', selectedBusinessId],
    async () => {
      if (!selectedBusinessId) return null;
      const response = await api.get('/api/tickets', {
        params: { business_id: selectedBusinessId, limit: 100 }
      });
      return response.data;
    },
    {
      enabled: !!selectedBusinessId && storeTicketsModalVisible
    }
  );

  const storeTickets = storeTicketsData?.tickets || [];

  if (isLoading && !feedbackData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error && !feedbackData) {
    return (
      <Alert
        message="Error"
        description={getFeedbackErrorMessage(error)}
        type="error"
        showIcon
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const monthlyStats = monthlyStatsData;
  const currentMonth = monthlyStats?.current_month;
  const lastMonth = monthlyStats?.last_month;

  const formatMonthName = (monthData: any) => {
    // Use month_formatted from backend if available (avoids timezone issues)
    if (monthData?.month_formatted) {
      return monthData.month_formatted;
    }
    // Fallback to parsing the timestamp
    if (monthData?.month) {
      const date = new Date(monthData.month);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return '';
  };

  return (
    <div style={{ marginLeft: '24px', marginRight: '24px' }}>
      <Title level={2}>Customer Feedback Dashboard</Title>

      {isError && feedbackData && (
        <Alert
          message="Unable to refresh feedback"
          description={getFeedbackErrorMessage(error)}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      )}

      {/* Monthly Statistics Comparison */}
      {monthlyStats && (
        <Card 
          title="Monthly Statistics Comparison" 
          style={{ marginBottom: '24px' }}
        >
          <Row gutter={[24, 16]}>
            {/* Current Month Column */}
            <Col xs={24} md={12}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0f9ff', 
                border: '2px solid #1890ff',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: '#1890ff',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  📅 {formatMonthName(currentMonth)} (This Month)
                </div>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Total Feedback"
                        value={currentMonth?.total_feedback || 0}
                        prefix={<StarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Average Rating"
                        value={currentMonth?.average_rating ? Number(currentMonth.average_rating).toFixed(1) : '0.0'}
                        suffix="/ 5"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Positive (4-5 stars)"
                        value={currentMonth?.positive_feedback || 0}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Negative (1-2 stars)"
                        value={currentMonth?.negative_feedback || 0}
                        valueStyle={{ color: '#ff4d4f' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Last Month Column */}
            <Col xs={24} md={12}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fafafa', 
                border: '2px solid #d9d9d9',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: '#595959',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  📅 {formatMonthName(lastMonth)} (Last Month)
                </div>
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Total Feedback"
                        value={lastMonth?.total_feedback || 0}
                        prefix={<StarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Average Rating"
                        value={lastMonth?.average_rating ? Number(lastMonth.average_rating).toFixed(1) : '0.0'}
                        suffix="/ 5"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Positive (4-5 stars)"
                        value={lastMonth?.positive_feedback || 0}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Card size="small" style={{ backgroundColor: '#fff' }}>
                      <Statistic
                        title="Negative (1-2 stars)"
                        value={lastMonth?.negative_feedback || 0}
                        valueStyle={{ color: '#ff4d4f' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Feedback"
                value={stats.total_feedback || 0}
                prefix={<StarOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Average Rating"
                value={stats.average_rating ? Number(stats.average_rating).toFixed(1) : '0.0'}
                suffix="/ 5"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              style={{ cursor: 'pointer', transition: 'all 0.3s' }}
              hoverable
              onClick={() => {
                setRatingFilter(ratingFilter === 'positive' ? null : 'positive');
                setFilters(prev => ({ ...prev, rating: undefined }));
              }}
              bodyStyle={{ 
                backgroundColor: ratingFilter === 'positive' ? '#f6ffed' : undefined,
                border: ratingFilter === 'positive' ? '2px solid #52c41a' : undefined
              }}
            >
              <Statistic
                title="Positive (4-5 stars)"
                value={stats.positive_feedback || 0}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card 
              style={{ cursor: 'pointer', transition: 'all 0.3s' }}
              hoverable
              onClick={() => {
                setRatingFilter(ratingFilter === 'negative' ? null : 'negative');
                setFilters(prev => ({ ...prev, rating: undefined }));
              }}
              bodyStyle={{ 
                backgroundColor: ratingFilter === 'negative' ? '#fff1f0' : undefined,
                border: ratingFilter === 'negative' ? '2px solid #ff4d4f' : undefined
              }}
            >
              <Statistic
                title="Negative (1-2 stars)"
                value={stats.negative_feedback || 0}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Space wrap>
          <FilterOutlined />
          <DatePicker.RangePicker
            placeholder={['Start Date', 'End Date']}
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
            allowClear
            style={{ width: 250 }}
          />
          <Select
            placeholder="Status"
            style={{ width: 150 }}
            value={filters.is_submitted === undefined ? undefined : filters.is_submitted}
            onChange={(value) => setFilters(prev => ({ 
              ...prev, 
              is_submitted: value === undefined ? undefined : value === true
            }))}
            allowClear
          >
            <Option value={true}>Submitted</Option>
            <Option value={false}>Pending</Option>
          </Select>
          
          <Select
            placeholder="Rating"
            style={{ width: 150 }}
            value={filters.rating}
            onChange={(value) => {
              setFilters(prev => ({ 
                ...prev, 
                rating: value
              }));
              setRatingFilter(null); // Clear positive/negative filter when selecting specific rating
            }}
            allowClear
          >
            <Option value={5}>5 Stars</Option>
            <Option value={4}>4 Stars</Option>
            <Option value={3}>3 Stars</Option>
            <Option value={2}>2 Stars</Option>
            <Option value={1}>1 Star</Option>
          </Select>

          <Select
            placeholder="Assignee"
            style={{ width: 200 }}
            value={filters.assignee_id}
            onChange={(value) => setFilters(prev => ({ 
              ...prev, 
              assignee_id: value
            }))}
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {agents.map((agent: any) => (
              <Option key={agent.id} value={agent.id}>
                {agent.first_name} {agent.last_name}
              </Option>
            ))}
          </Select>
        </Space>
      </Card>

      {/* Feedback Table */}
      <Table
        dataSource={feedback}
        rowKey="id"
        loading={isFetching}
        columns={[
          {
            title: 'Store',
            dataIndex: 'business_name',
            key: 'store',
            render: (text: string, record: FeedbackItem) => (
              <div>
                <button
                  type="button"
                  onClick={() => record.business_id && handleStoreClick(record.business_id, text)}
                  disabled={!record.business_id}
                  style={{ 
                    cursor: record.business_id ? 'pointer' : 'default', 
                    color: record.business_id ? '#1890ff' : 'inherit',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    textAlign: 'left',
                  }}
                >
                  {text}
                </button>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.contact_person}
                </div>
              </div>
            ),
          },
          {
            title: 'Ticket',
            dataIndex: 'ticket_number',
            key: 'ticket',
            render: (text: string, record: FeedbackItem) => (
              <div>
                <button
                  type="button"
                  onClick={() => handleTicketClick(record.ticket_number)}
                  style={{ cursor: 'pointer', color: '#1890ff', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                >
                  #{text}
                </button>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.ticket_subject}
                </div>
              </div>
            ),
          },
          {
            title: 'Assignee',
            key: 'assignee',
            render: (_, record: FeedbackItem) => 
              record.assignee_first_name && record.assignee_last_name 
                ? `${record.assignee_first_name} ${record.assignee_last_name}`
                : 'Unassigned',
          },
          {
            title: 'Rating',
            dataIndex: 'rating',
            key: 'rating',
            render: (rating: number, record: FeedbackItem) => 
              record.is_submitted ? (
                <Space>
                  <Rate disabled value={rating} />
                  <Tag color={getRatingColor(rating)}>{rating}</Tag>
                </Space>
              ) : (
                <span style={{ color: '#999' }}>Not rated</span>
              ),
          },
          {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
            render: (comment: string) => 
              comment ? (
                <div style={{ maxWidth: 200 }}>
                  {comment.length > 100 ? `${comment.substring(0, 100)}...` : comment}
                </div>
              ) : (
                <span style={{ color: '#999' }}>No comment</span>
              ),
          },
          {
            title: 'Date',
            dataIndex: 'submitted_at',
            key: 'date',
            render: (date: string, record: FeedbackItem) => 
              formatDate(date || record.created_at),
          },
          {
            title: 'Status',
            dataIndex: 'is_submitted',
            key: 'status',
            render: (isSubmitted: boolean, record: FeedbackItem) => (
              <Space>
                <Tag color={isSubmitted ? 'success' : 'warning'}>
                  {isSubmitted ? 'Submitted' : 'Pending'}
                </Tag>
                {record.is_archived && (
                  <Tag color="default">Archived</Tag>
                )}
              </Space>
            ),
          },
          {
            title: 'Follow-up',
            key: 'follow_up',
            render: (_: unknown, record: FeedbackItem) => {
              if (!record.is_submitted) {
                return <span style={{ color: '#999' }}>—</span>;
              }
              if (record.follow_up_assignee_first_name && record.follow_up_assignee_last_name) {
                return formatUserWithRole(
                  record.follow_up_assignee_first_name,
                  record.follow_up_assignee_last_name,
                  record.follow_up_assignee_role,
                  record.follow_up_assignee_agent_level,
                  record.follow_up_assignee_role_display_name
                );
              }
              return <Tag color="warning">Unassigned</Tag>;
            },
          },
          {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, record: FeedbackItem) => (
              <Dropdown menu={getFeedbackActionMenu(record)} trigger={['click']}>
                <Button type="text" icon={<MoreOutlined />} size="small" />
              </Dropdown>
            ),
          },
        ]}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: feedbackTotal,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          onChange: (page, pageSize) => {
            setPagination({
              current: page,
              pageSize: pageSize || pagination.pageSize,
            });
          },
        }}
        locale={{
          emptyText: 'No feedback found matching your filters.',
        }}
      />

      {/* Feedback Details Modal */}
      <Modal
        title="Feedback Details"
        open={modalVisible}
        onCancel={closeFeedbackModal}
        footer={[
          <Button key="close" onClick={closeFeedbackModal}>
            Close
          </Button>,
          detailFeedback && (
            <Button 
              key="ticket" 
              type="primary" 
              onClick={() => {
                navigate(`/workspace/number/${detailFeedback.ticket_number}`);
                closeFeedbackModal();
              }}
            >
              View Ticket
            </Button>
          ),
        ]}
        width={800}
      >
        {detailFeedback && (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Ticket">
                <Space>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/workspace/number/${detailFeedback.ticket_number}`);
                      closeFeedbackModal();
                    }}
                    style={{ cursor: 'pointer', color: '#1890ff', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                  >
                    #{detailFeedback.ticket_number}
                  </button>
                  <span style={{ color: '#666' }}>{detailFeedback.ticket_subject}</span>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Business">
                <div>
                  <div style={{ fontWeight: 500 }}>{detailFeedback.business_name}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {detailFeedback.contact_person}
                  </div>
                  {detailFeedback.business_email && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {detailFeedback.business_email}
                    </div>
                  )}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Assignee">
                {detailFeedback.assignee_first_name && detailFeedback.assignee_last_name
                  ? `${detailFeedback.assignee_first_name} ${detailFeedback.assignee_last_name}`
                  : 'Unassigned'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Space>
                  <Tag color={detailFeedback.is_submitted ? 'success' : 'warning'}>
                    {detailFeedback.is_submitted ? 'Submitted' : 'Pending'}
                  </Tag>
                  {detailFeedback.is_archived && (
                    <Tag color="default">Archived</Tag>
                  )}
                </Space>
              </Descriptions.Item>
              {detailFeedback.is_submitted && (
                <Descriptions.Item label="Rating">
                  <Space>
                    <Rate disabled value={detailFeedback.rating || 0} />
                    <Tag color={getRatingColor(detailFeedback.rating || 0)}>
                      {detailFeedback.rating} / 5
                    </Tag>
                  </Space>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Feedback Request Sent">
                {formatDate(detailFeedback.created_at)}
              </Descriptions.Item>
              {detailFeedback.submitted_at && (
                <Descriptions.Item label="Feedback Submitted">
                  {formatDate(detailFeedback.submitted_at)}
                </Descriptions.Item>
              )}
            </Descriptions>

            {detailFeedback.is_submitted && (
              <>
                <Divider />
                <div ref={followUpSectionRef}>
                  <Title level={5} style={{ marginTop: 0 }}>Follow-up</Title>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                      Assign an agent to reach out about this feedback
                    </Text>
                    <Select
                      placeholder="Select follow-up assignee"
                      style={{ width: '100%' }}
                      value={detailFeedback.follow_up_assignee_id || undefined}
                      onChange={(value) => followUpMutation.mutate(value || null)}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      loading={followUpMutation.isLoading}
                    >
                      {agents.map((agent: AgentOption) => {
                        const label = formatUserWithRole(
                          agent.first_name,
                          agent.last_name,
                          agent.role,
                          agent.agent_level,
                          agent.role_display_name
                        );
                        return (
                          <Option key={agent.id} value={agent.id} label={label}>
                            {label}
                          </Option>
                        );
                      })}
                    </Select>
                  </div>

                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Follow-up History
                  </Text>
                  {isLoadingDetail ? (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                      <Spin />
                    </div>
                  ) : detailComments.length === 0 ? (
                    <Text type="secondary">No comments yet.</Text>
                  ) : (
                    <List
                      style={{ maxHeight: 240, overflow: 'auto', marginBottom: 16 }}
                      dataSource={detailComments}
                      renderItem={(item: FeedbackComment) => (
                        <List.Item style={{ alignItems: 'flex-start' }}>
                          <List.Item.Meta
                            avatar={
                              <Avatar
                                icon={<UserOutlined />}
                                style={{
                                  backgroundColor: item.author_type === 'customer' ? '#87d068' : '#1890ff',
                                }}
                              />
                            }
                            title={
                              <Space>
                                <span>{item.author_name}</span>
                                <Tag color={item.author_type === 'customer' ? 'green' : 'blue'}>
                                  {item.author_type === 'customer' ? 'Customer' : 'Agent'}
                                </Tag>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {formatDate(item.created_at)}
                                </Text>
                              </Space>
                            }
                            description={
                              <div style={{ whiteSpace: 'pre-wrap' }}>{item.comment}</div>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  )}

                  <TextArea
                    rows={3}
                    placeholder="Add a follow-up note (e.g. contacted customer, reason for negative review)..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  <Button
                    type="primary"
                    onClick={() => {
                      if (newComment.trim()) {
                        addCommentMutation.mutate(newComment.trim());
                      }
                    }}
                    loading={addCommentMutation.isLoading}
                    disabled={!newComment.trim()}
                  >
                    Add Comment
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      {/* Store Tickets Modal */}
      <Modal
        title={`Tickets for ${selectedBusinessName}`}
        open={storeTicketsModalVisible}
        onCancel={() => {
          setStoreTicketsModalVisible(false);
          setSelectedBusinessId(null);
          setSelectedBusinessName('');
          // Remove business params from URL
          const params = new URLSearchParams(searchParams);
          params.delete('business_id');
          params.delete('business_name');
          setSearchParams(params, { replace: true });
        }}
        footer={[
          <Button key="close" onClick={() => {
            setStoreTicketsModalVisible(false);
            setSelectedBusinessId(null);
            setSelectedBusinessName('');
            // Remove business params from URL
            const params = new URLSearchParams(searchParams);
            params.delete('business_id');
            params.delete('business_name');
            setSearchParams(params, { replace: true });
          }}>
            Close
          </Button>
        ]}
        width={900}
      >
        {isLoadingStoreTickets ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : storeTickets.length === 0 ? (
          <Alert
            message="No tickets found"
            description={`No tickets found for ${selectedBusinessName}.`}
            type="info"
            showIcon
          />
        ) : (
          <Table
            dataSource={storeTickets}
            rowKey="id"
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tickets`,
            }}
            columns={[
              {
                title: 'Ticket #',
                dataIndex: 'ticket_number',
                key: 'ticket_number',
                render: (text: string) => (
                  <button
                    type="button"
                    onClick={() => {
                      // Navigate to ticket but preserve return state with business info
                      navigate(`/workspace/number/${text}`, {
                        state: {
                          returnTo: `/feedback-dashboard?business_id=${selectedBusinessId}&business_name=${encodeURIComponent(selectedBusinessName)}`
                        }
                      });
                    }}
                    style={{ cursor: 'pointer', color: '#1890ff', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
                  >
                    #{text}
                  </button>
                ),
              },
              {
                title: 'Subject',
                dataIndex: 'subject',
                key: 'subject',
                ellipsis: true,
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status: string) => {
                  const statusColors: Record<string, string> = {
                    new: 'default',
                    open: 'blue',
                    pending: 'orange',
                    solved: 'green',
                  };
                  return (
                    <Tag color={statusColors[status] || 'default'}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Tag>
                  );
                },
              },
              {
                title: 'Priority',
                dataIndex: 'priority',
                key: 'priority',
                render: (priority: string) => {
                  const priorityColors: Record<string, string> = {
                    low: 'green',
                    medium: 'orange',
                    high: 'red',
                    urgent: 'red',
                  };
                  return (
                    <Tag color={priorityColors[priority] || 'default'}>
                      {priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'N/A'}
                    </Tag>
                  );
                },
              },
              {
                title: 'Assignee',
                key: 'assignee',
                render: (_, record: any) => {
                  if (record.assigned_to_name && record.assigned_to_last_name) {
                    return `${record.assigned_to_name} ${record.assigned_to_last_name}`;
                  }
                  return <Tag color="default">Unassigned</Tag>;
                },
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (date: string) => formatDate(date),
              },
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default FeedbackDashboard;
